import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, viewChild } from '@angular/core';
import { PopoverDirective, PopoverModule } from 'ngx-bootstrap/popover';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { DrawingShape } from '../../../../core';
import { IsCubic, IsSquare } from '../../../../core/functions';
import { TakeOffMeasurement } from '../../../../domain';
import { PitchChangedEventArgs, TakeOffPitchDropdownModule } from '../../../../take-off/pitch-dropdown.component';
import { TakeOffConfig } from '../../domain';
import { TakeOffDrawingColorPickerComponent } from '../ui-take-off-drawing-color-picker/take-off-drawing-color-picker.component';
import { TakeOffDrawingDepthComponent } from '../ui-take-off-drawing-depth/take-off-drawing-depth.component';

@Component({
    selector: 'app-take-off-drawing-actions',
    templateUrl: './take-off-drawing-actions.component.html',
    styleUrls: ['./take-off-drawing-actions.component.scss'],
    imports: [
        CommonModule,
        TooltipModule,
        TakeOffPitchDropdownModule,
        TakeOffDrawingColorPickerComponent,
        PopoverModule,
        TakeOffDrawingDepthComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TakeOffDrawingActionsComponent {
    config = input.required<TakeOffConfig>();
    takeoffMeasurement = input<TakeOffMeasurement | null>(null);
    isDisabled = input<boolean>(false);

    popoverPolygon = viewChild<PopoverDirective>('popoverPolygon');
    popoverRectangle = viewChild<PopoverDirective>('popoverRectangle');
    popoverEllipse = viewChild<PopoverDirective>('popoverEllipse');
    popoverLine = viewChild<PopoverDirective>('popoverLine');

    shapeChanges = output<string>();
    colorChanges = output<string>();
    pitchChanges = output<PitchChangedEventArgs>();
    deductionChanges = output<void>();
    depthChanges = output<number>();
    depthUnitChanges = output<string>();
    pointEditChanges = output<void>();

    isDepthEnabled = computed(() => this.#checkIsDepthEnabled(this.config()?.uom?.toLowerCase(), this.config()?.shape));

    get #popovers() {
        return {
            Polygon: this.popoverPolygon,
            Rectangle: this.popoverRectangle,
            Ellipse: this.popoverEllipse,
            Line: this.popoverLine,
        } as const;
    }

    validPopOverShapes = ['Polygon', 'Rectangle', 'Ellipse', 'Line'];

    constructor() {
        effect(() => {
            if (this.isDepthEnabled() && this.takeoffMeasurement() && this.config()) {
                const shape = this.config()?.shape;
                if (!shape) {
                    this.#hideAllPopovers();
                    return;
                }
                this.#togglePopover(shape);
            } else {
                this.#hideAllPopovers();
            }
        });
    }

    #hideAllPopovers() {
        Object.values(this.#popovers).forEach((popover) => popover()?.hide());
    }

    #togglePopover(shape: string) {
        if (!this.validPopOverShapes.includes(shape)) return;
        const currentIsOpen = this.#popovers[shape as PopoverShape]()?.isOpen;
        if (currentIsOpen) return;

        this.#hideAllPopovers();
        setTimeout(() => this.#popovers[shape as PopoverShape]()?.show(), 1);
    }

    public onChangeShape(shape: string) {
        this.shapeChanges.emit(shape);
    }

    public onColorChanged(color: string) {
        this.colorChanges.emit(color);
    }

    public pitchChanged(pitch: PitchChangedEventArgs) {
        this.pitchChanges.emit(pitch);
    }

    public onDeduct() {
        this.deductionChanges.emit();
    }

    public onDepthChanged(depth: number) {
        this.depthChanges.emit(depth);
    }

    public onDepthUnitChanged(unit: string) {
        this.depthUnitChanges.emit(unit);
    }

    #checkIsDepthEnabled(unit: string, shape: string): boolean {
        return IsCubic(unit) || (IsSquare(unit) && shape === DrawingShape.Line);
    }

    public onAddPointModeChanged() {
        this.pointEditChanges.emit();
    }
}

type PopoverShape = 'Polygon' | 'Rectangle' | 'Ellipse' | 'Line';
